/**
 * RestrictedFeatureModalController — modale standard « Fonctionnalité
 * restreinte » (EPIC-038 / 38.4 — §2.K).
 *
 * Controller global monté sur le <body>. Expose
 * `window.RestrictedFeatureModal.open(action)` : n'importe quel
 * controller métier (deltas 12.9 / 13.10 …) ouvre la modale d'une seule
 * ligne, sans dupliquer la logique.
 *
 * Le balisage est la maquette 38.1 clonée verbatim depuis
 * `<template id="tpl-restricted-feature">`. Il est hébergé dans un
 * `<dialog class="nm-modal">` créé à la volée : `showModal()` offre
 * gratuitement focus-trap, fermeture `Esc`, `::backdrop` flouté et
 * arrière-plan inerte — aucune gestion de focus à la main. Le `<dialog>`
 * n'est qu'un hôte : sa chrome (bordure, fond, ombre) est neutralisée en
 * inline pour ne laisser voir que la carte `.nm-modal-mock`.
 *
 * Events émis via `window.NotamicroTracker` :
 *   - `restricted_modal_shown`        à l'ouverture, contexte `{ action }`
 *   - `restricted_modal_cta_activate` clic « Activer mon compte »
 *   - `restricted_modal_cta_continue` « Continuer en démo » / croix / `Esc`
 */

import BaseController from "./base_controller.js";

// CTA primaire §2.K — wizard de création INPI §18 (parcours A).
// L'EPIC citait `/inscription/parcours-a`, route inexistante (D3).
const ACTIVATE_URL = "/demarrage-creation.html";

export default class RestrictedFeatureModalController extends BaseController {
  connect() {
    // Exposé au montage : les appels `open()` sont déclenchés par un
    // clic utilisateur, toujours postérieur au `connect()`.
    window.RestrictedFeatureModal = { open: (action) => this.open(action) };
    this._dialog = null;
    this._previousOverflow = "";
  }

  disconnect() {
    this.close();
  }

  /**
   * Ouvre la modale pour l'action terminale `action` (ex.
   * `'quote.generate_pdf'`). Idempotent : un second appel pendant que la
   * modale est ouverte est ignoré.
   *
   * @param {string} action  Identifiant de l'action restreinte.
   */
  open(action) {
    if (this._dialog) return;

    const dialog = document.createElement("dialog");
    dialog.className = "nm-modal";
    // Hôte transparent : seule la carte .nm-modal-mock doit être visible.
    Object.assign(dialog.style, {
      border: "0",
      padding: "0",
      background: "transparent",
      boxShadow: "none",
      maxWidth: "none",
      width: "auto",
      overflow: "visible",
    });
    dialog.setAttribute("aria-labelledby", "restricted-feature-title");
    dialog.append(this.cloneTemplate("tpl-restricted-feature"));

    // CTA et fermetures — les boutons portent les marqueurs verbatim de la
    // maquette (`data-action="activate|continue"`, plain attributes
    // ignorés par Stimulus faute d'identifiant de controller).
    dialog.querySelector('[data-action="activate"]')
      ?.addEventListener("click", () => this._activate());
    dialog.querySelector('[data-action="continue"]')
      ?.addEventListener("click", () => this.close("continue"));
    dialog.querySelector(".nm-modal-mock__close")
      ?.addEventListener("click", () => this.close("continue"));
    // `Esc` (event `cancel`) et clic sur le backdrop → fermeture démo.
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      this.close("continue");
    });
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) this.close("continue");
    });

    document.body.append(dialog);
    this._dialog = dialog;
    this._previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.showModal();

    window.NotamicroTracker?.trackRestrictedModalShown(action);
  }

  /**
   * Ferme la modale et la retire du DOM. `reason === 'continue'` émet
   * `restricted_modal_cta_continue` (« Continuer en démo », croix, `Esc`,
   * backdrop). `_activate()` appelle `close()` sans `reason` : l'event
   * `activate` a déjà été émis.
   *
   * @param {string} [reason]
   */
  close(reason) {
    const dialog = this._dialog;
    if (!dialog) return;
    this._dialog = null;

    if (reason === "continue") {
      window.NotamicroTracker?.trackRestrictedCta("continue");
    }
    if (dialog.open) dialog.close();
    dialog.remove();
    document.body.style.overflow = this._previousOverflow;
  }

  _activate() {
    window.NotamicroTracker?.trackRestrictedCta("activate");
    this.close();
    // L'event part en `keepalive` : la navigation immédiate ne le perd pas.
    window.location.assign(ACTIVATE_URL);
  }
}
